import React, { useEffect, useRef, useState } from "react";

// Carga el script de Google Maps una sola vez y lo reutiliza entre montajes e
// instancias. Antes el script se inyectaba (y removía) en cada render, lo que
// disparaba el error "You have included the Google Maps JavaScript API multiple
// times on this page" y rompía el Autocomplete.
let mapsPromise = null;

function loadGoogleMaps(apiKey) {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window no disponible"));
  }
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve(window.google);
  }
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    // Google llama a esta función global cuando la autenticación de la key
    // falla (key inválida, referer no permitido o billing/API deshabilitados).
    window.gm_authFailure = () => {
      console.error(
        "[GoogleMaps] Falló la autenticación de la API key. Verifica que la key sea " +
          "válida, que el dominio del CMS esté permitido en las restricciones de referer " +
          "y que 'Maps JavaScript API' y 'Places API' estén habilitadas con billing activo " +
          "en Google Cloud Console."
      );
    };

    // Con loading=async el evento `load` del script se dispara ANTES de que la
    // librería `places` termine de cargar, así que hay que esperar a que
    // `google.maps.places.Autocomplete` exista; de lo contrario truena con
    // "Cannot read properties of undefined". Se hace por polling para ser
    // agnóstico al tipo de loader (async o clásico): no todos los bootstraps
    // exponen `importLibrary`.
    const waitForPlaces = () => {
      const start = Date.now();
      const check = () => {
        if (
          window.google &&
          window.google.maps &&
          window.google.maps.places &&
          window.google.maps.places.Autocomplete
        ) {
          resolve(window.google);
        } else if (Date.now() - start > 10000) {
          reject(new Error("La librería 'places' de Google Maps no se cargó a tiempo"));
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    };

    const existing = document.getElementById("google-maps-script");
    if (existing) {
      existing.addEventListener("load", waitForPlaces);
      existing.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = waitForPlaces;
    script.onerror = () => {
      mapsPromise = null; // permitir reintento en el próximo montaje
      reject(new Error("No se pudo cargar el script de Google Maps"));
    };
    document.head.appendChild(script);
  });

  return mapsPromise;
}

const PlacesAutocomplete = ({ apiKey, onSelect, defaultValue, disabled }) => {
  const [inputValue, setInputValue] = useState(defaultValue || "");
  const autocompleteRef = useRef(null);
  const onSelectRef = useRef(onSelect);

  // Mantén el callback siempre actualizado sin re-inicializar el Autocomplete
  // (onSelect suele cambiar de referencia en cada render del formulario).
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let autocomplete;
    let listener;
    let cancelled = false;

    loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled || !autocompleteRef.current) return;
        autocomplete = new google.maps.places.Autocomplete(autocompleteRef.current, {
          types: ["address"],
        });
        listener = autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place && place.geometry) {
            setInputValue(place.formatted_address);
            if (onSelectRef.current) {
              onSelectRef.current({
                address: place.formatted_address,
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              });
            }
          }
        });
      })
      .catch((err) => console.error("[GoogleMaps]", err.message));

    return () => {
      cancelled = true;
      if (listener && window.google) {
        window.google.maps.event.removeListener(listener);
      }
      // No se remueve el <script>: es un singleton reutilizado por toda la app.
    };
  }, [apiKey]);

  // Actualiza el valor inicial si `defaultValue` cambia.
  useEffect(() => {
    setInputValue(defaultValue || "");
  }, [defaultValue]);

  return (
    <input
      ref={autocompleteRef}
      type="text"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      placeholder="Av. Reforma 123, Colonia Juárez, Alcaldía Cuauhtémoc, Ciudad de México, C.P. 06600"
      className={`w-full pl-10 pr-3 py-3 border-[1px] rounded-[6px] placeholder:text-black focus:outline-none focus:ring focus:ring-grey ${
        disabled ? "bg-[#f3f4f6]" : "bg-white"
      }`}
      disabled={disabled}
    />
  );
};

export default PlacesAutocomplete;
export { loadGoogleMaps };
